/* assets/dyn-cart-recos.js
   Dynamic cart recommendations for Impact cart drawer

   BEHAVIOR:
   - ALWAYS show recos (independent of cart contents)
   - Candidates are ONLY taken from a collection section:
     /collections/<handle>?section_id=dyn-cart-collection
   - Mix Home/Fashion equally (does NOT depend on cart composition)
*/
(function () {
  const COLLECTION_SECTION_ID = 'dyn-cart-collection'; // must match sections/dyn-cart-collection.liquid
  const CACHE_TTL_MS = 5 * 60 * 1000;
  const SEED_SLOT_MS = 10 * 60 * 1000;

  // ------------------------------
  // small fetch + parsing helpers
  // ------------------------------
  async function fetchText(url, opts) {
    const res = await fetch(url, opts);
    return { ok: res.ok, status: res.status, text: await res.text() };
  }

  async function fetchJSON(url, opts) {
    const res = await fetch(url, opts);
    return { ok: res.ok, status: res.status, json: await res.json() };
  }

  function parseHTML(html) {
    const doc = document.implementation.createHTMLDocument('');
    doc.documentElement.innerHTML = html;
    return doc;
  }

  function getCartProductIds(cartJson) {
    const ids = new Set();
    (cartJson?.items || []).forEach((it) => {
      if (it && it.product_id) ids.add(String(it.product_id));
    });
    return ids;
  }

  function uniqByProductId(nodes) {
    const seen = new Set();
    const out = [];
    for (const n of nodes) {
      const pid = n.getAttribute('data-product-id');
      if (!pid) continue;
      if (seen.has(pid)) continue;
      seen.add(pid);
      out.push(n);
    }
    return out;
  }

  function nodeTrqType(node) {
    return String(node.getAttribute('data-trq-type') || '').trim().toLowerCase();
  }

  function isDrawerOpen() {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return false;
    return (
      drawer.hasAttribute('open') ||
      drawer.classList.contains('drawer--open') ||
      drawer.getAttribute('aria-hidden') === 'false'
    );
  }

  function refreshCarousel(carousel) {
    try {
      carousel.dispatchEvent(new Event('scroll:refresh', { bubbles: true }));
    } catch (e) {}
    try {
      window.dispatchEvent(new Event('resize'));
    } catch (e) {}
  }

  // ------------------------------
  // Manual nav binding (prev/next)
  // ------------------------------
  function bindManualNav(container) {
    const navEl = container.querySelector('[data-dyn-recos-nav]');
    const carousel = container.querySelector('scroll-carousel');
    if (!navEl || !carousel) return;

    if (navEl.__dynBound) return;
    navEl.__dynBound = true;

    const prevBtn = navEl.querySelector('button[is="prev-button"]');
    const nextBtn = navEl.querySelector('button[is="next-button"]');
    if (!prevBtn || !nextBtn) return;

    const getScrollEl = () => carousel.querySelector('.scroll-area') || carousel;

    const updateDisabled = () => {
      const scroller = getScrollEl();
      const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      const x = scroller.scrollLeft || 0;

      if (max <= 2) {
        prevBtn.disabled = true;
        nextBtn.disabled = true;
        return;
      }
      prevBtn.disabled = x <= 2;
      nextBtn.disabled = x >= max - 2;
    };

    const scrollByOneCard = (dir) => {
      const scroller = getScrollEl();
      const firstCard = carousel.querySelector('.horizontal-product');
      const step = firstCard ? firstCard.getBoundingClientRect().width + 12 : Math.max(220, scroller.clientWidth * 0.8);
      scroller.scrollBy({ left: dir * step, behavior: 'smooth' });
    };

    prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      scrollByOneCard(-1);
    });

    nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      scrollByOneCard(1);
    });

    const scroller = getScrollEl();
    scroller.addEventListener('scroll', updateDisabled, { passive: true });
    window.addEventListener('resize', updateDisabled);

    setTimeout(updateDisabled, 50);
  }

  // ------------------------------
  // Cart change detection (no loops)
  // ------------------------------
  function emitCartChanged() {
    try {
      document.dispatchEvent(new CustomEvent('dyn:cart-changed'));
    } catch (e) {}
  }

  (function hookFetchOnce() {
    if (window.__dynRecosFetchHooked) return;
    window.__dynRecosFetchHooked = true;

    const origFetch = window.fetch;
    window.fetch = async function (...args) {
      const res = await origFetch.apply(this, args);

      try {
        const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
        if (
          url.includes('/cart/add') ||
          url.includes('/cart/change') ||
          url.includes('/cart/update') ||
          url.includes('/cart/clear')
        ) {
          setTimeout(emitCartChanged, 0);
        }
      } catch (e) {}

      return res;
    };
  })();

  // ------------------------------
  // Seeded shuffle helpers
  // ------------------------------
  function mulberry32(seed) {
    let t = seed >>> 0;
    return function () {
      t += 0x6D2B79F5;
      let r = Math.imul(t ^ (t >>> 15), 1 | t);
      r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
      return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
    };
  }

  function hashStringToSeed(str) {
    let h = 2166136261;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }

  function shuffleInPlace(arr, rand) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      const tmp = arr[i];
      arr[i] = arr[j];
      arr[j] = tmp;
    }
    return arr;
  }

  // ------------------------------
  // Fetch candidates from collection section
  // ------------------------------
  async function fetchCollectionCandidates(handle) {
    if (!handle) return [];

    window.__dynRecosCollectionCache = window.__dynRecosCollectionCache || {};
    const cache = window.__dynRecosCollectionCache[handle];

    if (cache && cache.ts && Array.isArray(cache.nodes) && Date.now() - cache.ts < CACHE_TTL_MS) {
      return cache.nodes;
    }

    const url = `/collections/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(COLLECTION_SECTION_ID)}`;
    const res = await fetchText(url, { credentials: 'same-origin' });
    if (!res.ok) return [];

    const doc = parseHTML(res.text);
    const nodes = Array.from(doc.querySelectorAll('.dyn-cart-recos__item'));

    window.__dynRecosCollectionCache[handle] = { ts: Date.now(), nodes };
    return nodes;
  }

  // ------------------------------
  // Equal mixing for Home/Fashion
  // ------------------------------
  function mixEqualHomeFashion(candidates, limit, seedStr) {
    const rand = mulberry32(hashStringToSeed(seedStr));

    const fashion = [];
    const home = [];
    const other = [];

    for (const n of candidates) {
      const t = nodeTrqType(n);
      if (t === 'fashion') fashion.push(n);
      else if (t === 'home') home.push(n);
      else other.push(n);
    }

    shuffleInPlace(fashion, rand);
    shuffleInPlace(home, rand);
    shuffleInPlace(other, rand);

    const out = [];
    const seen = new Set();

    const take = (arr) => {
      while (arr.length) {
        const n = arr.shift();
        const pid = n.getAttribute('data-product-id');
        if (!pid) continue;
        if (seen.has(pid)) continue;
        seen.add(pid);
        out.push(n);
        return true;
      }
      return false;
    };

    // Alternate between fashion/home, random start
    let turn = rand() < 0.5 ? 'fashion' : 'home';

    while (out.length < limit && (fashion.length || home.length)) {
      if (turn === 'fashion') {
        if (!take(fashion)) take(home);
        turn = 'home';
      } else {
        if (!take(home)) take(fashion);
        turn = 'fashion';
      }
      if (!fashion.length && !home.length) break;
    }

    // Fill remaining
    while (out.length < limit && take(other)) {}
    while (out.length < limit && take(fashion)) {}
    while (out.length < limit && take(home)) {}

    return out.slice(0, limit);
  }

  // ------------------------------
  // Main loader
  // ------------------------------
  async function loadRecsInto(container) {
    if (container.__dynRecosLoading) return;
    container.__dynRecosLoading = true;

    try {
      const limit = Number(container.getAttribute('data-limit') || '10') || 10;

      const navEl = container.querySelector('[data-dyn-recos-nav]');
      const carousel = container.querySelector('scroll-carousel');
      const listEl = container.querySelector('[data-dyn-recos-list]');
      if (!carousel || !listEl) return;

      if (!isDrawerOpen()) {
        container.__dynRecosRetry = (container.__dynRecosRetry || 0) + 1;
        if (container.__dynRecosRetry <= 6) setTimeout(() => loadRecsInto(container), 250);
        return;
      }
      container.__dynRecosRetry = 0;

      const collectionHandle = (container.getAttribute('data-collection-handle') || '').trim();
      if (!collectionHandle) return;

      // candidates from collection section
      const rawCandidates = await fetchCollectionCandidates(collectionHandle);

      // fetch cart only to exclude items already in cart
      const cartRes = await fetchJSON('/cart.js', { credentials: 'same-origin' });
      if (!cartRes.ok) return;

      const cart = cartRes.json;
      const inCartIds = getCartProductIds(cart);

      const candidates = uniqByProductId(
        rawCandidates.filter((n) => {
          const pid = String(n.getAttribute('data-product-id') || '').trim();
          return pid && !inCartIds.has(pid);
        })
      );

      const cartSeedPart = (cart.items || []).map((it) => String(it.product_id)).sort().join(',');
      const timeSlot = Math.floor(Date.now() / SEED_SLOT_MS);
      const seedStr = `${collectionHandle}|${cartSeedPart}|${timeSlot}`;

      const mixed = mixEqualHomeFashion(candidates, limit, seedStr);

      if (!mixed.length) {
        carousel.setAttribute('hidden', '');
        if (navEl) navEl.setAttribute('hidden', '');
        return;
      }

      listEl.innerHTML = '';
      mixed.forEach((n) => listEl.appendChild(document.importNode(n, true)));

      carousel.removeAttribute('hidden');
      if (navEl) {
        if (mixed.length > 1) navEl.removeAttribute('hidden');
        else navEl.setAttribute('hidden', '');
      }

      bindManualNav(container);
      refreshCarousel(carousel);

      setTimeout(() => {
        bindManualNav(container);
        refreshCarousel(carousel);
      }, 0);
    } finally {
      container.__dynRecosLoading = false;
    }
  }

  // ------------------------------
  // Init + re-init hooks
  // ------------------------------
  function initContainer(container) {
    if (container.__dynRecosInit) return;
    container.__dynRecosInit = true;

    let t = null;
    const scheduleReload = () => {
      clearTimeout(t);
      t = setTimeout(() => loadRecsInto(container), 350);
    };

    bindManualNav(container);
    loadRecsInto(container);

    document.addEventListener('cart:refresh', scheduleReload);
    document.addEventListener('cart:updated', scheduleReload);
    document.addEventListener('cart:change', scheduleReload);
    document.addEventListener('dyn:cart-changed', scheduleReload);

    const drawer = document.getElementById('cart-drawer');
    if (drawer) {
      const mo = new MutationObserver(() => {
        if (isDrawerOpen()) scheduleReload();
      });
      mo.observe(drawer, { attributes: true, attributeFilter: ['open', 'class', 'aria-hidden'] });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-dyn-recos]').forEach(initContainer);
  }

  document.addEventListener('dyn:cart-changed', () => setTimeout(initAll, 0));

  (function observeDomOnce() {
    if (window.__dynRecosDomObserved) return;
    window.__dynRecosDomObserved = true;

    const obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const n of m.addedNodes || []) {
          if (!(n instanceof Element)) continue;
          if (n.matches?.('[data-dyn-recos]') || n.querySelector?.('[data-dyn-recos]') || n.id === 'cart-drawer' || n.querySelector?.('#cart-drawer')) {
            setTimeout(initAll, 0);
            return;
          }
        }
      }
    });

    obs.observe(document.documentElement, { childList: true, subtree: true });
  })();

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
})();